import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Zap, RefreshCw, AlertCircle } from 'lucide-react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  TooltipProps,
  Legend
} from 'recharts';
import { ValueType, NameType } from 'recharts/types/component/DefaultTooltipContent';
import { api as axios } from '../lib/api';
import { useI18n } from '../i18n/i18n';

interface PowerPoint {
  timestamp: string;
  power_consumption_kw: number | null;
  cooling_efficiency: number | null;
  pue: number | null;
  location: string | null;
}

interface PowerResponse {
  data: PowerPoint[];
  metadata: {
    schema: string;
    table: string;
    limit: number;
    period: string | null;
    from: string | null;
    to: string | null;
    location: string | null;
    count: number;
    generatedAt: string;
  };
}

const PERIOD_OPTIONS = ['4h', '12h', '24h', '3d', '1w', '1M', '2M', '3M', '6M', '12M'] as const;

type PeriodOption = (typeof PERIOD_OPTIONS)[number];

const numberFormatter = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1
});

const timeFormatter = (value: string, period: PeriodOption): string => {
  const date = new Date(value);
  const timestamp = date.getTime();
  if (!Number.isFinite(timestamp)) {
    return value;
  }
  // For longer periods, show date + time
  if (['3d', '1w', '1M', '2M', '3M', '6M', '12M'].includes(period)) {
    return date.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }
  // For short periods, show only time
  return date.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const tooltipFormatter: TooltipProps<ValueType, NameType>['formatter'] = (value, name) => {
  if (typeof value === 'number') {
    const unit = name === 'PUE' ? '' : ' kW';
    return [`${numberFormatter.format(value)}${unit}`, String(name ?? '')];
  }
  return [String(value), String(name ?? '')];
};

interface PowerAnalysisWidgetProps {
  period?: PeriodOption;
  refreshKey?: number;
  hideHeader?: boolean;
  rackId?: string;
  location?: string;
  dateFrom?: string;
  dateTo?: string;
}

const PowerAnalysisWidget: React.FC<PowerAnalysisWidgetProps> = ({
  period: externalPeriod,
  refreshKey = 0,
  hideHeader = false,
  rackId,
  location,
  dateFrom,
  dateTo
}) => {
  const { t } = useI18n();
  const isMountedRef = useRef(true);
  const [internalPeriod] = useState<PeriodOption>('24h');
  const period = externalPeriod || internalPeriod;
  const [data, setData] = useState<PowerPoint[]>([]);
  const [metadata, setMetadata] = useState<PowerResponse['metadata'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const fetchData = useCallback(async (selectedPeriod: PeriodOption, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      const params: any = { limit: 200 };

      // Use custom date range if provided, otherwise use period
      if (dateFrom && dateTo) {
        params.from = dateFrom;
        params.to = dateTo;
      } else {
        params.period = selectedPeriod;
      }

      if (rackId) params.rack_id = rackId;
      if (location) params.location = location;

      const response = await axios.get<PowerResponse>('/api/pue/timeseries', {
        params
      });
      if (!isMountedRef.current) {
        return;
      }
      setData(response.data.data);
      setMetadata(response.data.metadata);
    } catch (err) {
      console.error('Failed to fetch power analysis data:', err);
      if (!isMountedRef.current) {
        return;
      }
      setError(t('powerAnalysis.error.title'));
    } finally {
      if (!options?.silent && isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [t, rackId, location, dateFrom, dateTo]);

  useEffect(() => {
    fetchData(period).catch(() => {});
    const interval = window.setInterval(() => {
      fetchData(period, { silent: true }).catch(() => {});
    }, 5 * 60 * 1000);
    return () => window.clearInterval(interval);
  }, [fetchData, period, refreshKey]);

  const metrics = useMemo(() => {
    const validIT = data
      .map(point => point.power_consumption_kw)
      .filter((value): value is number => typeof value === 'number');
    const validCooling = data
      .map(point => point.cooling_efficiency)
      .filter((value): value is number => typeof value === 'number');

    if (!validIT.length || !validCooling.length) {
      return {
        avgIT: null, latestIT: null, totalIT: null,
        avgCooling: null, latestCooling: null, totalCooling: null,
        efficiency: null
      };
    }

    const avgIT = validIT.reduce((acc, value) => acc + value, 0) / validIT.length;
    const avgCooling = validCooling.reduce((acc, value) => acc + value, 0) / validCooling.length;
    const latestIT = validIT[validIT.length - 1];
    const latestCooling = validCooling[validCooling.length - 1];
    const totalIT = validIT.reduce((acc, value) => acc + value, 0);
    const totalCooling = validCooling.reduce((acc, value) => acc + value, 0);
    const efficiency = totalIT > 0 ? (totalCooling / totalIT) * 100 : null;

    return { avgIT, latestIT, totalIT, avgCooling, latestCooling, totalCooling, efficiency };
  }, [data]);

  const handleRefresh = () => {
    fetchData(period).catch(() => {});
  };

  return (
    <div className="card card-hover p-3 flex flex-col h-full overflow-hidden">
      {!hideHeader && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <Zap className="h-4 w-4 text-yellow-600" />
            <h3 className="text-sm font-semibold text-gray-900">{t('powerAnalysis.title')}</h3>
          </div>
          <button onClick={handleRefresh} className="btn-secondary text-xs flex items-center px-2 py-1">
            <RefreshCw className="h-3 w-3" />
          </button>
        </div>
      )}

      {error ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <AlertCircle className="h-8 w-8 text-error-500 mb-2" />
          <p className="text-sm font-medium text-error-600">{error}</p>
          <button onClick={handleRefresh} className="btn-primary mt-2 text-xs">{t('pue.error.retry')}</button>
        </div>
      ) : loading && !data.length ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <div className="loading-spinner h-6 w-6 mb-2" />
          <p className="text-gray-500 text-sm">Loading...</p>
        </div>
      ) : data.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center py-4">
          <AlertCircle className="h-8 w-8 text-gray-400 mb-2" />
          <p className="text-gray-500 text-sm">{t('powerAnalysis.noData')}</p>
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            <div className="bg-green-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-green-600 tracking-wide">{t('powerAnalysis.metrics.latestIT')}</p>
              <p className="text-sm font-semibold text-gray-900">
                {metrics.latestIT !== null ? `${numberFormatter.format(metrics.latestIT)} kW` : '--'}
              </p>
            </div>
            <div className="bg-blue-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-blue-600 tracking-wide">{t('powerAnalysis.metrics.latestCooling')}</p>
              <p className="text-sm font-semibold text-gray-900">
                {metrics.latestCooling !== null ? `${numberFormatter.format(metrics.latestCooling)} kW` : '--'}
              </p>
            </div>
            <div className="bg-yellow-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-yellow-600 tracking-wide">{t('powerAnalysis.metrics.efficiency')}</p>
              <p className="text-sm font-semibold text-gray-900">
                {metrics.efficiency !== null ? `${numberFormatter.format(metrics.efficiency)}%` : '--'}
              </p>
            </div>
            <div className="bg-purple-50 rounded p-1.5">
              <p className="text-[9px] uppercase text-purple-600 tracking-wide">{t('powerAnalysis.metrics.totalPower')}</p>
              <p className="text-sm font-semibold text-gray-900">
                {metrics.totalIT !== null && metrics.totalCooling !== null
                  ? `${numberFormatter.format(metrics.totalIT + metrics.totalCooling)} kW` : '--'}
              </p>
            </div>
          </div>

          {/* Chart */}
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height={240}>
              <ComposedChart data={data} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={(value) => timeFormatter(value, period)}
                  tick={{ fontSize: 8, fill: '#6b7280' }}
                  minTickGap={50}
                />
                <YAxis
                  yAxisId="power"
                  orientation="left"
                  tick={{ fontSize: 8, fill: '#6b7280' }}
                  tickFormatter={(value) => `${numberFormatter.format(value)} kW`}
                />
                <YAxis
                  yAxisId="pue"
                  orientation="right"
                  tick={{ fontSize: 8, fill: '#7c3aed' }}
                  tickFormatter={(value) => numberFormatter.format(value)}
                />
                <Tooltip
                  formatter={tooltipFormatter}
                  labelFormatter={(value) => timeFormatter(String(value), period)}
                  contentStyle={{ borderRadius: 6, fontSize: 9 }}
                />
                <Legend wrapperStyle={{ fontSize: 8, paddingTop: 0 }} />
                <Bar yAxisId="power" dataKey="power_consumption_kw" name={t('powerAnalysis.itPowerLegend')} fill="#10b981" opacity={0.8} />
                <Bar yAxisId="power" dataKey="cooling_efficiency" name={t('powerAnalysis.coolingPowerLegend')} fill="#3b82f6" opacity={0.8} />
                <Line yAxisId="pue" type="monotone" dataKey="pue" name={t('powerAnalysis.pueLegend')} stroke="#7c3aed" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="text-[10px] text-gray-500 mt-1">
            {t('pue.updated')} {metadata?.generatedAt ? new Date(metadata.generatedAt).toLocaleString() : ''}
          </div>
        </>
      )}
    </div>
  );
};

export default PowerAnalysisWidget;